import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle, RefreshCw, Plug, Facebook } from 'lucide-react';
import { connectMeta, MetaNotConfiguredError } from '@/lib/api/integrations';

/**
 * OAuth callback page (the META_OAUTH_REDIRECT_URI target,
 * /integrations/meta/callback).
 *
 * Facebook redirects the user here with ?code=...&state=... after the consent
 * dialog. We then need the Page ID to wire (the backend exchanges the code,
 * validates the page is one the user manages, stores the per-tenant page token,
 * and subscribes the webhook). Facebook's OAuth callback does not return the
 * selected page id, so we ask the admin to confirm the Page ID, then POST
 * {code, state, page_id} to /api/integrations/meta/connect.
 */
type Phase = 'collect' | 'connecting' | 'success' | 'error';

export function MetaCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const code = params.get('code') || '';
  const state = params.get('state') || '';
  // Facebook returns error / error_description on a denied or failed consent.
  const fbError = params.get('error_description') || params.get('error') || '';

  const [pageId, setPageId] = useState(params.get('page_id') || '');
  const [phase, setPhase] = useState<Phase>(fbError ? 'error' : 'collect');
  const [error, setError] = useState<string | null>(fbError || null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [connectedPageName, setConnectedPageName] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // Missing code/state means we got here outside the OAuth flow.
  const missingParams = !fbError && (!code || !state);

  const handleConnect = useCallback(async () => {
    if (!pageId.trim()) {
      setError('Facebook Page ID is required.');
      return;
    }
    setError(null);
    setNotConfigured(false);
    setPhase('connecting');
    try {
      const result = await connectMeta({ code, state, page_id: pageId.trim() });
      setConnectedPageName(result.page_name);
      setSubscribed(result.subscribed);
      setPhase('success');
    } catch (e) {
      if (e instanceof MetaNotConfiguredError) {
        setNotConfigured(true);
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to connect Facebook');
      }
      setPhase('error');
    }
  }, [code, state, pageId]);

  // Route back to the integrations page after a successful connect so the card
  // shows "Connected".
  useEffect(() => {
    if (phase !== 'success') return;
    const t = setTimeout(() => navigate('/integrations', { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [phase, navigate]);

  return (
    <div className="p-6 max-w-xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Facebook className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base">Connect Facebook</CardTitle>
              <CardDescription>Finish wiring your Facebook Page to the dialer.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {missingParams && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Missing the Facebook authorization details. Start the connection from
                the Integrations page.
              </span>
            </div>
          )}

          {phase === 'success' ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {connectedPageName} page connected
                  {subscribed ? ' and subscribed.' : '. The page token is stored; finish the webhook subscription if needed.'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Returning to Integrations...</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="page-id">Facebook Page ID</Label>
                <Input
                  id="page-id"
                  placeholder="e.g. 1234567890"
                  value={pageId}
                  onChange={e => setPageId(e.target.value)}
                  disabled={phase === 'connecting' || missingParams}
                />
                <p className="text-xs text-muted-foreground">
                  Find this on your Facebook Page under About → Page transparency,
                  or in Meta Business Suite → Settings.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {notConfigured
                      ? 'Meta app not configured yet. The platform Meta App ID/secret have not been set, so connecting is not available yet.'
                      : error}
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleConnect}
                  disabled={phase === 'connecting' || missingParams || notConfigured}
                  className="flex-1"
                >
                  {phase === 'connecting' ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Plug className="h-4 w-4 mr-2" /> Finish Connecting</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/integrations')}
                  disabled={phase === 'connecting'}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
